/**
 * SalesiveTracker — Browser Auto-Capture SDK
 *
 * Automatically captures user interactions, page activity, navigation,
 * and idle state, streaming structured events via Socket.IO.
 *
 * Usage:
 *   import { SalesiveTracker } from './autoTracker.js';
 *   const tracker = new SalesiveTracker(socketInstance, config);
 *   tracker.start();
 *   // later...
 *   tracker.stop();
 */

const SDK_VERSION = "1.0.0";

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG = {
    // Throttle intervals (ms)
    scrollThrottleMs: 300,
    mousemoveThrottleMs: 1000,
    inputDebounceMs: 400,
    hoverThrottleMs: 500,

    // Idle detection
    idleTimeoutMs: 30_000, // 30 seconds

    // Data attributes to pluck from elements
    trackedDataAttributes: [
        "data-track-id",
        "data-product-id",
        "data-category",
        "data-action",
        "data-label",
        "data-sku",
        "data-variant",
    ],

    // Socket event name for all tracker events
    socketEvent: "track:event",

    // Which auto-capture groups are enabled
    capture: {
        clicks: true,
        inputs: true,
        navigation: true,
        scroll: true,
        hover: true,
        visibility: true,
        idle: true,
        focus: true,
        submit: true,
    },

    // Fields that should never have their value captured
    sensitiveFieldTypes: ["password"],
    sensitiveFieldNames: [
        "password",
        "passwd",
        "secret",
        "credit_card",
        "creditcard",
        "card_number",
        "cardnumber",
        "cvv",
        "cvc",
        "ssn",
        "social_security",
    ],

    // Max queued events when socket is not yet connected
    maxQueueSize: 100,
};

// ─── Utility helpers ─────────────────────────────────────────────────────────

function throttle(fn, ms) {
    let last = 0;
    let timer = null;
    return function throttled(...args) {
        const now = Date.now();
        const remaining = ms - (now - last);
        if (remaining <= 0) {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            last = now;
            fn.apply(this, args);
        } else if (!timer) {
            timer = setTimeout(() => {
                last = Date.now();
                timer = null;
                fn.apply(this, args);
            }, remaining);
        }
    };
}

function debounce(fn, ms) {
    let timer = null;
    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
    };
}

// ─── Element identification ──────────────────────────────────────────────────

function getVisibleText(el) {
    if (!el) return "";
    // Prefer innerText (rendered) over textContent (source)
    const text = (el.innerText || el.textContent || "").trim();
    // Cap at reasonable length
    return text.length > 200 ? text.slice(0, 200) : text;
}

function getLabel(el) {
    if (!el) return "";
    // 1. Explicit <label for="...">
    if (el.id) {
        const label = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
        if (label) return getVisibleText(label);
    }
    // 2. Wrapping <label>
    const parentLabel = el.closest("label");
    if (parentLabel) {
        // Get label text excluding the input's own text
        const clone = parentLabel.cloneNode(true);
        const inputs = clone.querySelectorAll("input,select,textarea");
        inputs.forEach((inp) => inp.remove());
        return getVisibleText(clone);
    }
    // 3. aria-label
    return el.getAttribute("aria-label") || "";
}

function buildStableSelector(el) {
    if (!el || el === document.body || el === document.documentElement) return "";
    const parts = [];
    let current = el;
    while (current && current !== document.body && parts.length < 5) {
        let segment = current.tagName.toLowerCase();
        if (current.id) {
            segment += `#${CSS.escape(current.id)}`;
            parts.unshift(segment);
            break; // id is unique enough
        }
        // nth-child for disambiguation
        const parent = current.parentElement;
        if (parent) {
            const siblings = Array.from(parent.children).filter(
                (c) => c.tagName === current.tagName,
            );
            if (siblings.length > 1) {
                const idx = siblings.indexOf(current) + 1;
                segment += `:nth-of-type(${idx})`;
            }
        }
        parts.unshift(segment);
        current = current.parentElement;
    }
    return parts.join(" > ");
}

function identifyElement(el, trackedDataAttributes) {
    if (!el || !el.tagName) return null;

    const dataAttrs = {};
    for (const attr of trackedDataAttributes) {
        const val = el.getAttribute(attr);
        if (val != null) dataAttrs[attr] = val;
    }

    return {
        tag: el.tagName.toLowerCase(),
        id: el.id || null,
        classList: el.className
            ? String(el.className)
                  .split(/\s+/)
                  .filter(Boolean)
            : [],
        text: getVisibleText(el),
        ariaLabel: el.getAttribute("aria-label") || null,
        title: el.getAttribute("title") || null,
        name: el.getAttribute("name") || null,
        role: el.getAttribute("role") || el.role || null,
        href: el.tagName === "A" ? el.getAttribute("href") : undefined,
        type: el.getAttribute("type") || undefined,
        dataAttributes: Object.keys(dataAttrs).length > 0 ? dataAttrs : undefined,
        selector: buildStableSelector(el),
    };
}

/**
 * Walk up from a raw event target to find the most meaningful clickable element.
 * Prefers interactive/semantic elements over generic containers.
 */
function findMeaningfulTarget(el) {
    const meaningful = new Set([
        "A",
        "BUTTON",
        "INPUT",
        "SELECT",
        "TEXTAREA",
        "LABEL",
        "SUMMARY",
        "DETAILS",
    ]);
    let current = el;
    while (current && current !== document.body) {
        if (
            meaningful.has(current.tagName) ||
            current.getAttribute("role") === "button" ||
            current.getAttribute("role") === "link" ||
            current.hasAttribute("data-track-id") ||
            current.getAttribute("tabindex") != null ||
            current.onclick != null
        ) {
            return current;
        }
        current = current.parentElement;
    }
    // Fallback to original target
    return el;
}

// ─── Metadata builder ────────────────────────────────────────────────────────
// Session identity is derived server-side from the socket JWT.
// The tracker only attaches client-observable context.

function buildMeta() {
    const nav = navigator;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

    return {
        timestamp: new Date().toISOString(),
        url: location.href,
        pathname: location.pathname,
        referrer: document.referrer || null,
        screen: {
            width: screen.width,
            height: screen.height,
        },
        viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
        },
        userAgent: nav.userAgent,
        language: nav.language,
        platform: nav.platform,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceMemory: nav.deviceMemory ?? null,
        cpuCores: nav.hardwareConcurrency ?? null,
        online: nav.onLine,
        network: conn
            ? {
                  effectiveType: conn.effectiveType ?? null,
                  downlink: conn.downlink ?? null,
                  rtt: conn.rtt ?? null,
                  saveData: conn.saveData ?? null,
              }
            : null,
        sdkVersion: SDK_VERSION,
    };
}

// ─── Main Tracker Class ─────────────────────────────────────────────────────

export class SalesiveTracker {
    /**
     * @param {object} deps — external dependencies
     * @param {function} deps.ensureConnected — returns Promise<socket>; handles auth, connects if needed
     * @param {function} [deps.getSocket] — returns current socket instance (for fast-path when already connected)
     * @param {object} config — override defaults
     */
    constructor(deps = {}, config = {}) {
        this._getSocket = deps.getSocket || null;
        this._config = { ...DEFAULT_CONFIG, ...config };
        if (config.capture) {
            this._config.capture = { ...DEFAULT_CONFIG.capture, ...config.capture };
        }
        if (config.trackedDataAttributes) {
            this._config.trackedDataAttributes = config.trackedDataAttributes;
        }

        this._running = false;
        this._cleanups = [];
        this._queue = []; // events queued before socket is ready

        // Idle state
        this._idle = false;
        this._lastActivityTs = Date.now();
        this._idleTimer = null;

        // Navigation state
        this._lastUrl = location.href;

        // Scroll tracking
        this._lastScrollData = null;

        // Input debounce map: element -> timer
        this._inputTimers = new Map();
    }

    // ── Public API ───────────────────────────────────────────────────────────

    /**
     * Update dependencies at runtime (e.g. when socket hook initializes after tracker).
     */
    setDeps(deps) {
        if (deps.getSocket) this._getSocket = deps.getSocket;
        // Flush any queued events now that we have a socket path
        this._flushQueue();
    }

    start() {
        if (this._running) return;
        this._running = true;

        const cfg = this._config.capture;

        // Watch for socket connect to flush any queued events
        this._watchSocketConnect();

        // Emit initial page_view
        this._emitPageView("initial");

        // ── Click / DblClick ─────────────────────────────────────────────
        if (cfg.clicks) {
            this._listen(document, "click", (e) => this._handleClick(e, "click"), true);
            this._listen(document, "dblclick", (e) => this._handleClick(e, "dblclick"), true);
        }

        // ── Input / Change / Focus / Blur ────────────────────────────────
        if (cfg.inputs) {
            this._listen(document, "input", (e) => this._handleInput(e), true);
            this._listen(document, "change", (e) => this._handleChange(e), true);
        }
        if (cfg.focus) {
            this._listen(document, "focusin", (e) => this._handleFocusBlur(e, "focus"), true);
            this._listen(document, "focusout", (e) => this._handleFocusBlur(e, "blur"), true);
        }

        // ── Submit ───────────────────────────────────────────────────────
        if (cfg.submit) {
            this._listen(document, "submit", (e) => this._handleSubmit(e), true);
        }

        // ── Scroll ───────────────────────────────────────────────────────
        if (cfg.scroll) {
            const scrollHandler = throttle(
                (e) => this._handleScroll(e),
                this._config.scrollThrottleMs,
            );
            this._listen(document, "scroll", scrollHandler, { passive: true, capture: true });
        }

        // ── Hover ────────────────────────────────────────────────────────
        if (cfg.hover) {
            const hoverHandler = throttle(
                (e) => this._handleHover(e),
                this._config.hoverThrottleMs,
            );
            this._listen(document, "mouseover", hoverHandler, true);
        }

        // ── Visibility ──────────────────────────────────────────────────
        if (cfg.visibility) {
            this._listen(document, "visibilitychange", () => this._handleVisibility());
        }

        // ── Navigation ──────────────────────────────────────────────────
        if (cfg.navigation) {
            this._patchHistory();
            this._listen(window, "popstate", () =>
                this._handleNavigation("popstate"),
            );
            this._listen(window, "hashchange", () =>
                this._handleNavigation("hashchange"),
            );
        }

        // ── Idle detection ──────────────────────────────────────────────
        if (cfg.idle) {
            const activitySignals = [
                "mousemove",
                "mousedown",
                "keydown",
                "scroll",
                "touchstart",
            ];
            const activityHandler = throttle(() => this._onActivity(), 1000);
            for (const evt of activitySignals) {
                this._listen(document, evt, activityHandler, { passive: true, capture: true });
            }
            this._resetIdleTimer();
        }

        // ── Page leave ──────────────────────────────────────────────────
        this._listen(window, "beforeunload", () => this._handlePageLeave());
    }

    stop() {
        if (!this._running) return;
        this._running = false;

        // Run all cleanup functions
        for (const cleanup of this._cleanups) {
            cleanup();
        }
        this._cleanups = [];

        // Clear idle timer
        if (this._idleTimer) {
            clearTimeout(this._idleTimer);
            this._idleTimer = null;
        }

        // Restore history if patched
        this._unpatchHistory();

        // Clear input timers
        for (const timer of this._inputTimers.values()) {
            clearTimeout(timer);
        }
        this._inputTimers.clear();
    }

    destroy() {
        this.stop();
        this._getSocket = null;
        this._queue = [];
    }

    // ── Event emission ──────────────────────────────────────────────────────

    _emit(eventType, data) {
        if (!this._running) return;

        const payload = {
            event: eventType,
            ...data,
            meta: buildMeta(),
        };

        // If socket is connected, send immediately
        const socket = this._getSocket?.();
        if (socket?.connected) {
            socket.emit(this._config.socketEvent, payload);
            return;
        }

        // Socket not connected yet — queue and let _watchSocketConnect flush
        // when the connection is established
        this._enqueue(payload);
    }

    _enqueue(payload) {
        if (this._queue.length >= this._config.maxQueueSize) {
            // Drop oldest to prevent unbounded growth
            this._queue.shift();
        }
        this._queue.push(payload);
    }

    _flushQueue() {
        if (this._queue.length === 0) return;

        const socket = this._getSocket?.();
        if (!socket?.connected) return;

        const pending = this._queue.splice(0);
        for (const payload of pending) {
            socket.emit(this._config.socketEvent, payload);
        }
    }

    /**
     * Poll for the socket to appear and listen for 'connect' to flush queued events.
     * This handles the race where the tracker starts before the socket is created/connected.
     */
    _watchSocketConnect() {
        this._connectPoll = setInterval(() => {
            const socket = this._getSocket?.();
            if (!socket) return;

            // Socket instance exists — stop polling, attach connect listener
            clearInterval(this._connectPoll);
            this._connectPoll = null;

            const onConnect = () => this._flushQueue();
            socket.on("connect", onConnect);
            this._cleanups.push(() => socket.off("connect", onConnect));

            // If already connected right now, flush immediately
            if (socket.connected) {
                this._flushQueue();
            }
        }, 200);

        // Safety: stop polling after 60s
        this._connectPollTimeout = setTimeout(() => {
            if (this._connectPoll) {
                clearInterval(this._connectPoll);
                this._connectPoll = null;
            }
        }, 60_000);

        this._cleanups.push(() => {
            if (this._connectPoll) {
                clearInterval(this._connectPoll);
                this._connectPoll = null;
            }
            if (this._connectPollTimeout) {
                clearTimeout(this._connectPollTimeout);
                this._connectPollTimeout = null;
            }
        });
    }

    // ── Listener management ─────────────────────────────────────────────────

    _listen(target, event, handler, optionsOrCapture) {
        target.addEventListener(event, handler, optionsOrCapture);
        this._cleanups.push(() =>
            target.removeEventListener(event, handler, optionsOrCapture),
        );
    }

    // ── Click handling ──────────────────────────────────────────────────────

    _handleClick(e, type) {
        const raw = e.target;
        if (!raw || !raw.tagName) return;

        const target = findMeaningfulTarget(raw);
        const elInfo = identifyElement(target, this._config.trackedDataAttributes);
        if (!elInfo) return;

        this._emit(type, {
            element: elInfo,
            position: {
                x: e.clientX,
                y: e.clientY,
                pageX: e.pageX,
                pageY: e.pageY,
            },
        });
    }

    // ── Input handling ──────────────────────────────────────────────────────

    _isSensitiveField(el) {
        const type = (el.getAttribute("type") || "").toLowerCase();
        if (this._config.sensitiveFieldTypes.includes(type)) return true;
        const name = (el.getAttribute("name") || "").toLowerCase();
        return this._config.sensitiveFieldNames.some(
            (s) => name.includes(s),
        );
    }

    _buildInputData(el) {
        const value = this._isSensitiveField(el) ? "[redacted]" : el.value;
        return {
            field: {
                tag: el.tagName.toLowerCase(),
                id: el.id || null,
                name: el.getAttribute("name") || null,
                type: el.getAttribute("type") || null,
                placeholder: el.getAttribute("placeholder") || null,
                label: getLabel(el),
                value,
            },
        };
    }

    _handleInput(e) {
        const el = e.target;
        if (!el || !("value" in el)) return;

        // Debounce per element
        const existing = this._inputTimers.get(el);
        if (existing) clearTimeout(existing);

        this._inputTimers.set(
            el,
            setTimeout(() => {
                this._inputTimers.delete(el);
                this._emit("input", this._buildInputData(el));
            }, this._config.inputDebounceMs),
        );
    }

    _handleChange(e) {
        const el = e.target;
        if (!el || !("value" in el)) return;

        // Clear any pending input debounce for this element
        const existing = this._inputTimers.get(el);
        if (existing) {
            clearTimeout(existing);
            this._inputTimers.delete(el);
        }

        this._emit("change", this._buildInputData(el));
    }

    // ── Focus / Blur ────────────────────────────────────────────────────────

    _handleFocusBlur(e, type) {
        const el = e.target;
        if (!el || !el.tagName) return;
        // Only track form-ish elements
        const tag = el.tagName;
        if (tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") return;

        this._emit(type, {
            element: identifyElement(el, this._config.trackedDataAttributes),
        });
    }

    // ── Submit ──────────────────────────────────────────────────────────────

    _handleSubmit(e) {
        const form = e.target;
        if (!form || form.tagName !== "FORM") return;

        this._emit("submit", {
            element: identifyElement(form, this._config.trackedDataAttributes),
            action: form.getAttribute("action") || null,
            method: (form.getAttribute("method") || "GET").toUpperCase(),
        });
    }

    // ── Scroll ──────────────────────────────────────────────────────────────

    _handleScroll() {
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop;
        const scrollHeight = doc.scrollHeight;
        const clientHeight = doc.clientHeight;
        const scrollPercent =
            scrollHeight > clientHeight
                ? Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
                : 0;

        this._emit("scroll", {
            scrollTop: Math.round(scrollTop),
            scrollPercent,
            direction: this._lastScrollData
                ? scrollTop > this._lastScrollData.scrollTop
                    ? "down"
                    : "up"
                : "down",
        });

        this._lastScrollData = { scrollTop };
    }

    // ── Hover ───────────────────────────────────────────────────────────────

    _handleHover(e) {
        const el = e.target;
        if (!el || !el.tagName) return;
        // Only emit for meaningful elements, not every <div>
        const target = findMeaningfulTarget(el);
        if (target === el && !el.id && !el.getAttribute("data-track-id")) return;

        this._emit("hover", {
            element: identifyElement(
                target,
                this._config.trackedDataAttributes,
            ),
        });
    }

    // ── Visibility ──────────────────────────────────────────────────────────

    _handleVisibility() {
        this._emit("visibility_change", {
            state: document.visibilityState,
        });
    }

    // ── Navigation ──────────────────────────────────────────────────────────

    _originalPushState = null;
    _originalReplaceState = null;

    _patchHistory() {
        // Wrap pushState and replaceState to detect SPA navigations
        this._originalPushState = history.pushState;
        this._originalReplaceState = history.replaceState;

        const self = this;

        history.pushState = function patchedPushState(...args) {
            self._originalPushState.apply(this, args);
            self._handleNavigation("pushState");
        };

        history.replaceState = function patchedReplaceState(...args) {
            self._originalReplaceState.apply(this, args);
            self._handleNavigation("replaceState");
        };
    }

    _unpatchHistory() {
        if (this._originalPushState) {
            history.pushState = this._originalPushState;
            this._originalPushState = null;
        }
        if (this._originalReplaceState) {
            history.replaceState = this._originalReplaceState;
            this._originalReplaceState = null;
        }
    }

    _handleNavigation(source) {
        const prevUrl = this._lastUrl;
        const nextUrl = location.href;

        // Avoid duplicates when URL hasn't actually changed
        if (prevUrl === nextUrl && source === "replaceState") return;

        this._lastUrl = nextUrl;

        this._emit("navigation", {
            source,
            from: prevUrl,
            to: nextUrl,
        });

        // Every navigation triggers a page_view
        this._emitPageView(source);
    }

    _emitPageView(source) {
        this._emit("page_view", {
            source,
            title: document.title,
        });
    }

    // ── Page leave ──────────────────────────────────────────────────────────

    _handlePageLeave() {
        this._emit("page_leave", {
            timeOnPage: Date.now() - (this._pageEnteredAt || Date.now()),
        });
    }

    _pageEnteredAt = Date.now();

    // ── Idle detection ──────────────────────────────────────────────────────

    _onActivity() {
        const wasIdle = this._idle;
        const now = Date.now();

        if (wasIdle) {
            const idleDuration = now - this._lastActivityTs;
            this._idle = false;
            this._emit("active", {
                idleDuration,
                lastActiveAt: new Date(this._lastActivityTs).toISOString(),
                idleTimeoutUsed: this._config.idleTimeoutMs,
            });
        }

        this._lastActivityTs = now;
        this._resetIdleTimer();
    }

    _resetIdleTimer() {
        if (this._idleTimer) clearTimeout(this._idleTimer);
        this._idleTimer = setTimeout(() => {
            this._idle = true;
            this._emit("idle", {
                lastActiveAt: new Date(this._lastActivityTs).toISOString(),
                idleTimeoutUsed: this._config.idleTimeoutMs,
            });
        }, this._config.idleTimeoutMs);
    }
}

// ─── Factory helper (non-React) ─────────────────────────────────────────────

/**
 * Create and start a tracker using the global window.SalesiveSocket.
 * Polls for the socket if it isn't available yet, then wires ensureConnected.
 * Returns the tracker instance for later control.
 *
 *   import { startTracking } from './autoTracker.js';
 *   const tracker = startTracking({ idleTimeoutMs: 60000 });
 */
export function startTracking(config = {}) {
    const deps = {
        getSocket: () => window.SalesiveSocket || null,
    };

    const tracker = new SalesiveTracker(deps, config);
    tracker.start();
    return tracker;
}

export default SalesiveTracker;
